import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Stack,
  useNumberInput,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  HStack,
  Flex,
} from '@chakra-ui/react';
import { Text } from '@outdoorly/text';
import { IconButton, ButtonNextLink } from '@outdoorly/button';
import { Container } from '@outdoorly/container';
import {
  ShoppingCartIcon,
  FlareIcon,
  PlusIcon,
  MinusIcon,
} from '@outdoorly/icons';
import { TextInput } from '@outdoorly/input';
import { Card } from '@outdoorly/card';
import { Divider } from '@outdoorly/divider';
import { OrderSummaryTable, ProductLine } from '@outdoorly/order';
import propTypes from 'prop-types';
import { useCheckout } from '../hooks/use-checkout';
import { Cluster } from '@outdoorly/cluster';
import * as ga from '../lib/ga';
import { getAnalyticsFromCheckout } from '../lib/ga/utils';

const EmptyCart = () => (
  <Stack spacing={8} align="center" py={10}>
    <Box position="relative" width="max-content">
      <ShoppingCartIcon width="16" height="16" />
      <FlareIcon
        transform="scaleX(-1)"
        position="absolute"
        left="calc(100% - 0.5em)"
        bottom="calc(100% - 1.5em)"
        color="scheme.iconRegular"
        width="12"
        height="12"
        strokeWidth={3}
      />
    </Box>
    <Stack spacing={4} align="center">
      <Text textTransform="uppercase" size="2xl" fontWeight="500">
        Shopping Cart
      </Text>
      <Text color="scheme.textSecondary">Your cart is empty</Text>
    </Stack>

    <ButtonNextLink variant="solid" colorScheme="red" route="/">
      Get shopping!
    </ButtonNextLink>
  </Stack>
);

const NumberStepper = ({ defaultValue, ...props }) => {
  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      min: 0,
      max: 99,
      defaultValue,
      clampValueOnBlur: false,
      ...props,
    });

  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps({ isReadOnly: true });

  const buttonProps = {
    variant: 'outline',
    colorScheme: 'gray',
    maxHeight: '80%',
    width: '80%',
    minWidth: '80%',
    maxWidth: '80%',
    borderWidth: 0,
    borderRadius: 'md',
    paddingX: 0,
  };

  return (
    <InputGroup width="6rem" size="sm">
      <InputLeftElement left={1}>
        <IconButton
          {...buttonProps}
          {...dec}
          aria-label="decrease quantity"
          icon={<MinusIcon />}
        />
      </InputLeftElement>
      <TextInput {...input} textAlign="center" borderRadius="md" />
      <InputRightElement right={1}>
        <IconButton
          {...buttonProps}
          {...inc}
          aria-label="increase quantity"
          icon={<PlusIcon />}
        />
      </InputRightElement>
    </InputGroup>
  );
};

NumberStepper.propTypes = {
  defaultValue: propTypes.number,
};
const FullCart = () => {
  const { checkout, updateCheckoutLine } = useCheckout();

  const updateTimer = useRef(null);

  // it would be better to use output from the updateCheckoutLine
  // mutation directly, but it's a lot more work to pull it
  // out of useCheckout and this works okay.
  const [isUpdating, setIsUpdating] = useState(false);
  const debouncedUpdateCheckoutLine = useCallback(
    (id, quantity) => {
      if (updateTimer.current) {
        clearTimeout(updateTimer.current);
      }
      updateTimer.current = setTimeout(() => {
        setIsUpdating(true);
        updateCheckoutLine(id, quantity).then(() => setIsUpdating(false));
      }, 200);
    },
    [updateCheckoutLine, updateTimer]
  );

  return (
    <Container>
      <Stack
        spacing={4}
        direction={['column', 'column', 'column', 'row']}
        justify="center"
      >
        <Card>
          <Stack spacing={4} minWidth={['full', 'full', '38rem']} width="full">
            <Stack spacing={4} divider={<Divider />}>
              <Text fontWeight="500" size="lg">
                Your cart
              </Text>
              {checkout?.brandCheckouts.map((brandCheckout) => (
                <Stack spacing={6} key={brandCheckout.id}>
                  <Box>
                    <Cluster gapX={2}>
                      <Text fontWeight="500">{brandCheckout.brand.name}</Text>
                      <Text color="scheme.textSecondary">{`${
                        brandCheckout.quantity
                      } ${
                        brandCheckout.quantity === 1 ? 'item' : 'items'
                      }`}</Text>
                    </Cluster>
                  </Box>
                  <Stack spacing={4}>
                    {brandCheckout.lines.map((line) => (
                      <ProductLine key={line.id} line={line}>
                        <Stack>
                          <NumberStepper
                            defaultValue={line.quantity}
                            max={line.variant.quantity ?? 50}
                            onChange={(string, number) => {
                              debouncedUpdateCheckoutLine(
                                line.variant.id,
                                number
                              );
                            }}
                          />
                        </Stack>
                      </ProductLine>
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Card>
        <Stack
          spacing={2}
          minWidth={['full', 'full', 'full', '21rem', '23rem']}
        >
          <Card>
            <Stack spacing={4}>
              <HStack>
                <Text fontWeight="500" size="lg">
                  Cart Summary
                </Text>
                <Text fontWeight="500" size="md" color="scheme.textSecondary">
                  {`${checkout?.quantity} ${
                    checkout?.quantity === 1 ? 'item' : 'items'
                  } `}
                </Text>
                {isUpdating && <Spinner size="xs" />}
              </HStack>
              <Divider />
              <OrderSummaryTable
                summary={{
                  items: checkout?.pricing.subtotalPrice.net.amount,
                  shipping: checkout?.pricing.shippingPrice.net.amount,
                  tax: checkout?.pricing.totalPrice.tax.amount,
                  totalDiscount: checkout?.pricing.savings?.amount,
                  total: checkout?.pricing.totalMinusStoreCredit.gross.amount,
                }}
                currency={checkout?.pricing.totalPrice.currency}
                includesShipping={false}
              />
            </Stack>
          </Card>
          <ButtonNextLink
            variant="solid"
            colorScheme="red"
            route="/checkout"
            onClick={() =>
              ga.event({
                action: 'begin_checkout',
                ...getAnalyticsFromCheckout(checkout),
              })
            }
          >
            Go to checkout
          </ButtonNextLink>
        </Stack>
      </Stack>
    </Container>
  );
};

FullCart.propTypes = {
  items: propTypes.arrayOf(propTypes.object).isRequired,
};

export const Cart = ({ items }) => {
  const { checkout, initialLoad } = useCheckout();

  return (
    <Flex grow={1} height="100%" justify="center" py={10}>
      {initialLoad ? (
        <Container>
          <Card align="center">
            <Spinner />
          </Card>
        </Container>
      ) : checkout?.quantity ? (
        <FullCart items={items} />
      ) : (
        <EmptyCart />
      )}
    </Flex>
  );
};

Cart.propTypes = {
  // TODO: Cart items need proptypes
  items: propTypes.arrayOf(propTypes.object),
};
